import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DataSnapshot } from "../lib/types";
import type { RunKind, RunRecord, RunsBundle } from "../lib/runs-types";
import { RUN_KIND_LABELS } from "../lib/runs-types";
import { loadRuns } from "../lib/api";
import { BrandRail } from "../components/BrandRail";
import { formatWhen } from "../lib/format";
import { useRoute } from "../router";
import { useFilters } from "../store/filters";

interface Props {
  data: DataSnapshot;
}

const ALL_KINDS: RunKind[] = [
  "portfolio",
  "pipeline",
  "scoring_batch",
  "autoresearch",
  "quality_gate",
  "telemetry",
];

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  success: { label: "SUCCESS",  cls: "bg-good text-bg" },
  failed:  { label: "FAILED",   cls: "bg-bad text-bg" },
  partial: { label: "PARTIAL",  cls: "bg-warn text-fg" },
  measured:{ label: "MEASURED", cls: "bg-fg text-bg" },
};

export function Runs({ data }: Props) {
  const { stats } = data;
  const [, navigate] = useRoute();
  const select = useFilters((s) => s.select);
  const { data: runsBundle, isLoading, error } = useQuery({
    queryKey: ["runs"],
    queryFn: loadRuns,
  });

  const [kindFilter, setKindFilter] = useState<RunKind | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!runsBundle) return [];
    let list = runsBundle.runs;
    if (kindFilter !== "all") list = list.filter((r) => r.kind === kindFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.label.toLowerCase().includes(q) ||
          r.id.toLowerCase().includes(q) ||
          r.kind.includes(q) ||
          (r.source?.toLowerCase().includes(q) ?? false),
      );
    }
    return list;
  }, [runsBundle, kindFilter, search]);

  const selected = useMemo(
    () => filtered.find((r) => r.id === selectedId) ?? filtered[0] ?? null,
    [filtered, selectedId],
  );

  const openSkill = (name: string) => {
    select(name);
    navigate({ name: "monitor" });
  };

  const total = runsBundle?.runs.length ?? 0;
  const success = runsBundle?.runs.filter((r) => r.status === "success").length ?? 0;
  const failed = runsBundle?.runs.filter((r) => r.status === "failed").length ?? 0;
  const partial = runsBundle?.runs.filter((r) => r.status === "partial").length ?? 0;

  return (
    <div className="runs-grid grid min-h-screen" style={{ gridTemplateColumns: "220px 320px 1fr" }}>
      <BrandRail
        counts={{
          monitor: stats.total,
          issues: (stats.statusCounts?.warning ?? 0) + (stats.statusCounts?.critical ?? 0),
        }}
      />

      <aside className="border-r border-fg flex flex-col min-h-screen">
        <header className="p-4 border-b border-fg">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-1">
            // Audit trail
          </span>
          <h1 className="font-display font-extrabold text-t-40 leading-[0.9] tracking-[-0.04em]">
            Runs
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted mt-2">
            {total} total · <span className="text-good">{success} ok</span> · <span className="text-warn">{partial} partial</span> · <span className="text-bad">{failed} fail</span>
          </p>
        </header>

        {isLoading && (
          <p className="p-4 font-mono text-[11px] text-muted uppercase">// loading runs…</p>
        )}
        {error && (
          <p className="p-4 font-mono text-[11px] text-bad uppercase">// failed to load runs.json</p>
        )}

        {runsBundle && (
          <>
            <div className="p-3 border-b border-rule space-y-2">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter runs…"
                className="w-full border border-fg px-3 py-2 font-mono text-[12px] bg-bg focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <div className="flex flex-wrap gap-1">
                <KindChip active={kindFilter === "all"} onClick={() => setKindFilter("all")} label="All" count={runsBundle.runs.length} />
                {ALL_KINDS.map((k) => {
                  const n = runsBundle.summary.byKind[k] ?? 0;
                  if (!n) return null;
                  return (
                    <KindChip
                      key={k}
                      active={kindFilter === k}
                      onClick={() => setKindFilter(k)}
                      label={RUN_KIND_LABELS[k]}
                      count={n}
                    />
                  );
                })}
              </div>
            </div>

            <ul className="flex-1 overflow-y-auto">
              {filtered.length === 0 ? (
                <li className="p-4 font-mono text-[11px] text-muted uppercase">// no runs match</li>
              ) : (
                filtered.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(r.id)}
                      className={`w-full text-left px-4 py-3 border-b border-rule transition-colors ${
                        selected?.id === r.id ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[9px] uppercase tracking-[0.14em] opacity-80">
                          {RUN_KIND_LABELS[r.kind]}
                        </span>
                        <StatusDot status={r.status} inverted={selected?.id === r.id} />
                      </div>
                      <div className="font-display font-bold text-[14px] mt-1 leading-tight">{r.label}</div>
                      <div className="font-mono text-[10px] mt-1 opacity-70 tabular-nums">
                        {formatWhen(r.timestamp)}
                        {r.gitSha ? ` · ${r.gitSha}` : ""}
                      </div>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </aside>

      <main className="min-h-screen overflow-y-auto">
        {!runsBundle && !isLoading ? (
          <EmptyBuildHint />
        ) : selected ? (
          <RunDetail run={selected} bundle={runsBundle!} onOpenSkill={openSkill} />
        ) : (
          <div className="p-8 font-mono text-[11px] uppercase text-muted">// select a run</div>
        )}

        {runsBundle && runsBundle.workflows.length > 0 && (
          <section className="border-t border-fg p-6 bg-[var(--hover)]">
            <h2 className="font-display font-bold text-t-20 mb-4 tracking-[-0.02em]">GitHub Actions workflows</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl">
              {runsBundle.workflows.map((w) => (
                <a
                  key={w.id}
                  href={w.actionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-fg p-4 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[3px_3px_0_0_var(--fg)] transition-all duration-[80ms] bg-bg"
                >
                  <div className="font-display font-bold">{w.name}</div>
                  <code className="font-mono text-[10px] text-muted uppercase mt-1 block tracking-[0.1em]">{w.file}</code>
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function KindChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 font-mono text-[9px] uppercase tracking-[0.1em] border border-fg ${
        active ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
      }`}
    >
      {label} {count}
    </button>
  );
}

function StatusDot({ status, inverted }: { status: string; inverted?: boolean }) {
  const cls =
    status === "success" ? "bg-good" :
    status === "failed"  ? "bg-bad"  :
    status === "partial" ? "bg-warn" :
    inverted ? "bg-bg" : "bg-muted";
  return <span className={`inline-block w-2 h-2 ${cls}`} title={status} />;
}

function StatusPill({ status }: { status: string }) {
  const m = STATUS_PILL[status] ?? { label: status.toUpperCase(), cls: "bg-muted text-bg" };
  return (
    <span className={`inline-block px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] font-bold border border-fg ${m.cls}`}>
      {m.label}
    </span>
  );
}

function EmptyBuildHint() {
  return (
    <div className="p-12">
      <h2 className="font-display text-t-40 font-extrabold leading-[0.9] tracking-[-0.04em]">No runs data.</h2>
      <p className="mt-4 font-mono text-[11px] uppercase text-muted">
        // run <code className="text-accent">npm run build:data</code> to generate runs.json
      </p>
    </div>
  );
}

function RunDetail({
  run,
  bundle,
  onOpenSkill,
}: {
  run: RunRecord;
  bundle: RunsBundle;
  onOpenSkill: (name: string) => void;
}) {
  const [tab, setTab] = useState<"overview" | "changes" | "log" | "entries">("overview");

  const tabs = useMemo(() => {
    const t: Array<"overview" | "changes" | "log" | "entries"> = ["overview"];
    if (run.skillChanges && run.skillChanges.length > 0) t.push("changes");
    if (run.log) t.push("log");
    if (run.entries && run.entries.length > 0) t.push("entries");
    return t;
  }, [run]);

  const metrics = run.metrics ?? {};
  const metricCount = Object.keys(metrics).length;
  const changeCount = run.skillChanges?.length ?? 0;

  return (
    <div>
      <header className="p-6 border-b border-fg sticky top-0 bg-bg z-10">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
            {RUN_KIND_LABELS[run.kind]} · {run.id}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
            {metricCount} metrics · {changeCount} skill changes
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <h2 className="font-display font-extrabold text-t-56 leading-[0.9] tracking-[-0.04em] max-w-3xl">
            {run.label}
          </h2>
          <div className="ml-auto pb-1 flex flex-col items-end gap-2">
            <StatusPill status={run.status} />
            <p className="font-mono text-[10px] uppercase text-muted tabular-nums">
              {formatWhen(run.timestamp)}
            </p>
          </div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted mt-3">
          {run.gitSha ? `git ${run.gitSha}` : ""}
          {run.source ? ` · ${run.source}` : ""}
          {" · "}bundle {formatWhen(bundle.generated)}
        </p>

        <div className="flex border border-fg mt-4 w-fit">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.1em] border-r border-fg last:border-r-0 ${
                tab === t ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      <div className="p-6">
        {tab === "overview" && <OverviewPanel run={run} />}
        {tab === "changes" && run.skillChanges && (
          <ChangesTable changes={run.skillChanges} onOpenSkill={onOpenSkill} />
        )}
        {tab === "log" && run.log && <LogPanel log={run.log} />}
        {tab === "entries" && run.entries && (
          <EntriesTable entries={run.entries} onOpenSkill={onOpenSkill} />
        )}
      </div>
    </div>
  );
}

function OverviewPanel({ run }: { run: RunRecord }) {
  const metrics = run.metrics ?? {};
  const delta = run.delta;
  const flags = run.flags;

  return (
    <div className="space-y-6 max-w-4xl">
      {Object.keys(metrics).length > 0 && (
        <section>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-3">// Metrics</h3>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(metrics).map(([key, val]) => (
              <MetricCard key={key} label={key} value={val} />
            ))}
          </dl>
        </section>
      )}

      {delta && typeof delta === "object" && (
        <section>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-3">// Delta vs previous</h3>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(delta).map(([key, val]) => (
              <MetricCard key={key} label={key} value={val} />
            ))}
          </dl>
        </section>
      )}

      {flags && (
        <section>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-3">// Scoring flags</h3>
          <pre className="border border-fg p-4 font-mono text-[11px] overflow-x-auto bg-[var(--hover)]">
            {JSON.stringify(flags, null, 2)}
          </pre>
        </section>
      )}

      {run.detail != null && (
        <section>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted mb-3">// Raw detail (JSON)</h3>
          <pre className="border border-fg p-4 font-mono text-[10px] overflow-x-auto max-h-[480px] overflow-y-auto bg-[var(--hover)]">
            {JSON.stringify(run.detail, null, 2)}
          </pre>
        </section>
      )}

      {Object.keys(metrics).length === 0 && !delta && !flags && run.detail == null && (
        <p className="font-mono text-[11px] text-muted uppercase">// no metadata captured for this run</p>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: unknown }) {
  const display =
    typeof value === "object" && value !== null
      ? JSON.stringify(value)
      : String(value ?? "—");
  return (
    <div className="border border-fg p-3">
      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted">{label}</dt>
      <dd className="font-mono text-[13px] font-medium mt-1 tabular-nums break-all">{display}</dd>
    </div>
  );
}

function ChangesTable({
  changes,
  onOpenSkill,
}: {
  changes: NonNullable<RunRecord["skillChanges"]>;
  onOpenSkill: (name: string) => void;
}) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b-2 border-fg">
          <Th>Skill</Th>
          <Th>Before</Th>
          <Th>After</Th>
          <Th>Δ</Th>
        </tr>
      </thead>
      <tbody>
        {changes.map((c) => (
          <tr key={c.name} className="border-b border-rule hover:bg-[var(--hover)]">
            <Td>
              <button type="button" className="text-accent hover:underline font-mono text-[12px]" onClick={() => onOpenSkill(c.name)}>
                {c.name}
              </button>
            </Td>
            <Td mono>{c.before ?? "—"}</Td>
            <Td mono>{c.after}</Td>
            <Td mono className={c.delta != null && c.delta > 0 ? "text-good" : c.delta != null && c.delta < 0 ? "text-bad" : ""}>
              {c.delta != null ? (c.delta > 0 ? `+${c.delta}` : c.delta) : "new"}
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EntriesTable({
  entries,
  onOpenSkill,
}: {
  entries: NonNullable<RunRecord["entries"]>;
  onOpenSkill: (name: string) => void;
}) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b-2 border-fg">
          <Th>Skill</Th>
          <Th>Value</Th>
          <Th>Status</Th>
          <Th>Description</Th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, i) => (
          <tr key={`${e.skill}-${i}`} className="border-b border-rule hover:bg-[var(--hover)]">
            <Td>
              <button type="button" className="text-accent hover:underline font-mono text-[12px]" onClick={() => onOpenSkill(e.skill)}>
                {e.skill}
              </button>
            </Td>
            <Td mono>{e.value}</Td>
            <Td mono>{e.status}</Td>
            <Td mono muted>{e.description ?? "—"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LogPanel({ log }: { log: string }) {
  const [showFull, setShowFull] = useState(true);
  const lines = log.split("\n");
  const preview = showFull ? lines : lines.slice(0, 80);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase text-muted">
          // {lines.length} lines · pipeline log
        </span>
        {lines.length > 80 && (
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            className="font-mono text-[10px] uppercase border border-fg px-3 py-1 hover:bg-[var(--hover)]"
          >
            {showFull ? "Collapse" : "Show full log"}
          </button>
        )}
      </div>
      <pre className="border border-fg p-4 font-mono text-[11px] leading-relaxed overflow-x-auto max-h-[70vh] overflow-y-auto whitespace-pre-wrap bg-[var(--hover)]">
        {preview.join("\n")}
        {!showFull && lines.length > 80 ? `\n// … ${lines.length - 80} more lines` : ""}
      </pre>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.14em] text-muted font-medium">
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  muted,
  className = "",
}: {
  children: React.ReactNode;
  mono?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`p-3 ${mono ? "font-mono text-[12px] tabular-nums" : ""} ${muted ? "text-muted" : ""} ${className}`}
    >
      {children}
    </td>
  );
}
